import { useState } from 'react';
import { Download, Maximize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { sendCustomEvent, sendErrorEvent } from '@Analytics';
import { Skeleton } from '@Components/ui-v2/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@Components/ui-v2/tooltip';
import { useFileInlineUrl, useGeneratedFileDownload } from '@CustomHooks';
import { GeneratedFile } from '@Models';
import FileDownloadButton from '../../FileDownloadButton/FileDownloadButton';

interface ImageAttachmentProps {
  file: GeneratedFile;
  conversationId: string;
  timestamp: string;
  /** Position in a sibling list — drives the staggered fade-up animation. */
  index?: number;
  /** Extra seconds added so the cascade can chain after sibling elements (e.g. option cards). */
  staggerOffset?: number;
  onExpand: (file: GeneratedFile, url: string) => void;
}

const ICON_BUTTON =
  'flex h-8 w-8 items-center justify-center rounded-md border-0 bg-white/90 text-gray-700 shadow-sm ' +
  'cursor-pointer transition-colors hover:bg-gray-200 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40';

const ImageAttachment = ({
  file,
  conversationId,
  timestamp,
  index = 0,
  staggerOffset = 0,
  onExpand,
}: ImageAttachmentProps) => {
  const { t } = useTranslation();
  const { download, isPending } = useGeneratedFileDownload(conversationId, timestamp, file);
  const [loaded, setLoaded] = useState(false);
  const [refreshed, setRefreshed] = useState(false);
  const [failed, setFailed] = useState(false);

  const inlineUrl = useFileInlineUrl({ conversationId, timestamp, fileName: file.file_name, enabled: !failed });

  if (failed || inlineUrl.isError) {
    return (
      <FileDownloadButton
        file={file}
        conversationId={conversationId}
        timestamp={timestamp}
        index={index}
        staggerOffset={staggerOffset}
      />
    );
  }

  const url = inlineUrl.data?.download_url;

  const handleImageError = () => {
    if (!refreshed) {
      // The presign may have expired in an idle tab: refresh once, then give up.
      setRefreshed(true);
      inlineUrl.refetch().catch(() => {});
      return;
    }
    sendErrorEvent(`agent image failed to load: ${file.file_type}`);
    setFailed(true);
  };

  const handleExpand = () => {
    if (!url) return;
    sendCustomEvent('AGENT_IMAGE_EXPAND', { fileType: file.file_type });
    onExpand(file, url);
  };

  return (
    <div
      className="group relative w-full max-w-full overflow-hidden rounded-lg border border-solid border-gray-200 bg-white animate-journey-slide-up"
      style={{ animationDelay: `${index * 0.08 + 0.05 + staggerOffset}s` }}>
      {!loaded && (
        <Skeleton className="relative aspect-video w-full overflow-hidden rounded-none">
          <div className="absolute inset-y-0 -left-1/4 w-3/4 animate-skeleton-shimmer bg-gradient-to-r from-transparent via-white/90 to-transparent" />
        </Skeleton>
      )}
      {url && (
        <button
          type="button"
          aria-label={t('Agent.ImageExpand')}
          onClick={handleExpand}
          className={`m-0 w-full cursor-zoom-in border-0 bg-transparent p-0 ${loaded ? 'block' : 'hidden'}`}>
          <img
            src={url}
            alt={file.file_name}
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={handleImageError}
            className="block w-full"
          />
        </button>
      )}
      {loaded && (
        <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 max-sm:opacity-100">
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label={t('Agent.ImageExpand')} onClick={handleExpand} className={ICON_BUTTON}>
                <Maximize2 className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipPrimitive.Portal>
              <TooltipContent side="bottom" className="pointer-events-none z-[140] text-xs">
                {t('Agent.ImageExpand')}
              </TooltipContent>
            </TooltipPrimitive.Portal>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={t('Agent.ImageDownload')}
                onClick={download}
                disabled={isPending}
                className={ICON_BUTTON}>
                <Download className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipPrimitive.Portal>
              <TooltipContent side="bottom" className="pointer-events-none z-[140] text-xs">
                {t('Agent.ImageDownload')}
              </TooltipContent>
            </TooltipPrimitive.Portal>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default ImageAttachment;
